import React, { useState, useEffect } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import './InsuranceWaiverManagement.css';

// ============================================================
// INSURANCE MANAGEMENT COMPONENT (Admin/Coach View)
// ============================================================

function InsuranceManagement() {
  const toast = useToast();
  const [policies, setPolicies] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  const [newPolicy, setNewPolicy] = useState({
    policyType: 'general_liability',
    providerName: '',
    policyNumber: '',
    coverageAmount: '',
    deductible: '',
    startDate: '',
    expirationDate: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    autoRenew: false,
    notes: ''
  });

  const [certificateFile, setCertificateFile] = useState(null);
  const [policyDocFile, setPolicyDocFile] = useState(null);

  useEffect(() => {
    fetchInsuranceData();
  }, []);

  const fetchInsuranceData = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('ams_user') || '{}');
      const teamId = user.teamId || 1;

      const [policiesRes, alertsRes] = await Promise.all([
        api.get(`/api/insurance/${teamId}`),
        api.get(`/api/insurance/${teamId}/alerts`)
      ]);

      setPolicies(policiesRes.data);
      setAlerts(alertsRes.data);
    } catch (err) {
      console.error('Error fetching insurance data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPolicy = async () => {
    try {
      const formData = new FormData();
      
      // Add all policy fields
      Object.keys(newPolicy).forEach(key => {
        formData.append(key, newPolicy[key]);
      });

      // Add files
      if (certificateFile) {
        formData.append('certificate', certificateFile);
      }
      if (policyDocFile) {
        formData.append('policy_document', policyDocFile);
      }

      await api.post('/api/insurance/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Insurance policy uploaded successfully!');
      setShowUploadModal(false);
      resetForm();
      fetchInsuranceData();

    } catch (err) {
      console.error('Error uploading policy:', err);
      toast.error('Failed to upload policy');
    }
  };

  const resetForm = () => {
    setNewPolicy({
      policyType: 'general_liability',
      providerName: '',
      policyNumber: '',
      coverageAmount: '',
      deductible: '',
      startDate: '',
      expirationDate: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      autoRenew: false,
      notes: ''
    });
    setCertificateFile(null);
    setPolicyDocFile(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: '✅ Active',
      expiring_soon: '⚠️ Expiring Soon',
      expired: '❌ Expired',
      pending_renewal: '⏳ Pending Renewal'
    };
    return badges[status] || status;
  };

  const getAlertBadge = (level) => {
    const badges = {
      CRITICAL: { emoji: '🚨', color: 'red', text: 'CRITICAL' },
      URGENT: { emoji: '⚠️', color: 'orange', text: 'URGENT' },
      WARNING: { emoji: '⏰', color: 'yellow', text: 'WARNING' }
    };
    return badges[level] || { emoji: '', color: 'gray', text: level };
  };

  return (
    <div className="insurance-management">
      <div className="page-header">
        <h1>🛡️ Insurance & Liability Management</h1>
        <button onClick={() => setShowUploadModal(true)} className="btn-primary">
          ➕ Upload Insurance Policy
        </button>
      </div>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="alerts-section">
          <h2>⚠️ Insurance Alerts</h2>
          <div className="alerts-grid">
            {alerts.map(alert => {
              const badge = getAlertBadge(alert.alert_level);
              return (
                <div key={alert.id} className={`alert-card alert-${badge.color}`}>
                  <div className="alert-header">
                    <span className="alert-badge">{badge.emoji} {badge.text}</span>
                    <span className="alert-days">{alert.days_until_expiration} days</span>
                  </div>
                  <h3>{alert.policy_type.replace(/_/g, ' ').toUpperCase()}</h3>
                  <p>{alert.provider_name}</p>
                  <p className="policy-number">Policy: {alert.policy_number}</p>
                  <p className="expiration">Expires: {new Date(alert.expiration_date).toLocaleDateString()}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Policies List */}
      <div className="policies-section">
        <h2>Insurance Policies</h2>
        {loading ? (
          <div className="loading">Loading policies...</div>
        ) : (
          <div className="policies-table">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Provider</th>
                  <th>Policy Number</th>
                  <th>Coverage</th>
                  <th>Start Date</th>
                  <th>Expiration</th>
                  <th>Days Left</th>
                  <th>Documents</th>
                </tr>
              </thead>
              <tbody>
                {policies.map(policy => (
                  <tr key={policy.id} className={policy.status === 'expired' ? 'expired-row' : ''}>
                    <td><span className="status-badge">{getStatusBadge(policy.status)}</span></td>
                    <td>{policy.policy_type.replace(/_/g, ' ')}</td>
                    <td>{policy.provider_name}</td>
                    <td>{policy.policy_number}</td>
                    <td>${policy.coverage_amount?.toLocaleString()}</td>
                    <td>{new Date(policy.start_date).toLocaleDateString()}</td>
                    <td>{new Date(policy.expiration_date).toLocaleDateString()}</td>
                    <td className={policy.days_until_expiration < 30 ? 'warning-text' : ''}>
                      {policy.days_until_expiration}
                    </td>
                    <td>
                      {policy.certificate_path && (
                        <a href={`${API_BASE_URL}${policy.certificate_path}`} target="_blank" rel="noopener noreferrer">
                          📄 Certificate
                        </a>
                      )}
                      {policy.policy_document_path && (
                        <a href={`${API_BASE_URL}${policy.policy_document_path}`} target="_blank" rel="noopener noreferrer" style={{marginLeft: '10px'}}>
                          📋 Policy
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Insurance Policy</h2>
              <button onClick={() => setShowUploadModal(false)} className="close-btn">✕</button>
            </div>
            
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Policy Type *</label>
                  <select
                    value={newPolicy.policyType}
                    onChange={(e) => setNewPolicy({...newPolicy, policyType: e.target.value})}
                  >
                    <option value="general_liability">General Liability</option>
                    <option value="professional_liability">Professional Liability</option>
                    <option value="property">Property Insurance</option>
                    <option value="workers_comp">Workers Compensation</option>
                    <option value="directors_officers">Directors & Officers</option>
                    <option value="abuse_molestation">Abuse & Molestation</option>
                    <option value="equipment">Equipment Insurance</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Provider Name *</label>
                  <input
                    type="text"
                    value={newPolicy.providerName}
                    onChange={(e) => setNewPolicy({...newPolicy, providerName: e.target.value})}
                    placeholder="State Farm, Allstate, etc."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Policy Number *</label>
                  <input
                    type="text"
                    value={newPolicy.policyNumber}
                    onChange={(e) => setNewPolicy({...newPolicy, policyNumber: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Coverage Amount *</label>
                  <input
                    type="number"
                    value={newPolicy.coverageAmount}
                    onChange={(e) => setNewPolicy({...newPolicy, coverageAmount: e.target.value})}
                    placeholder="1000000"
                  />
                </div>

                <div className="form-group">
                  <label>Deductible</label>
                  <input
                    type="number"
                    value={newPolicy.deductible}
                    onChange={(e) => setNewPolicy({...newPolicy, deductible: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={newPolicy.startDate}
                    onChange={(e) => setNewPolicy({...newPolicy, startDate: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Expiration Date *</label>
                  <input
                    type="date"
                    value={newPolicy.expirationDate}
                    onChange={(e) => setNewPolicy({...newPolicy, expirationDate: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Agent/Contact Name</label>
                  <input
                    type="text"
                    value={newPolicy.contactName}
                    onChange={(e) => setNewPolicy({...newPolicy, contactName: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    value={newPolicy.contactPhone}
                    onChange={(e) => setNewPolicy({...newPolicy, contactPhone: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Contact Email</label>
                  <input
                    type="email"
                    value={newPolicy.contactEmail}
                    onChange={(e) => setNewPolicy({...newPolicy, contactEmail: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Upload Certificate of Insurance (PDF/Image) *</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setCertificateFile(e.target.files[0])}
                />
                {certificateFile && <span className="file-name">✓ {certificateFile.name}</span>}
              </div>

              <div className="form-group">
                <label>Upload Full Policy Document (Optional)</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setPolicyDocFile(e.target.files[0])}
                />
                {policyDocFile && <span className="file-name">✓ {policyDocFile.name}</span>}
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newPolicy.autoRenew}
                    onChange={(e) => setNewPolicy({...newPolicy, autoRenew: e.target.checked})}
                  />
                  {' '}Auto-renew policy
                </label>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newPolicy.notes}
                  onChange={(e) => setNewPolicy({...newPolicy, notes: e.target.value})}
                  rows="3"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowUploadModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleUploadPolicy} className="btn-primary">
                Upload Policy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InsuranceManagement;
